import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { LogIn, LogOut, Home, History, Mail, Menu, X, UserPlus, Gavel, Newspaper, User, ChevronDown, ChevronUp, FileText, Upload, Building, ShoppingCart } from 'lucide-react';

const Header = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    onLogout();
    navigate('/');
    setMobileMenuOpen(false);
    setProfileDropdownOpen(false);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setProfileDropdownOpen(false);
  };

  // CPF do administrador
  const isAdmin = user?.cpf?.replace(/\D/g, '') === '00000000000';

  const navigationItems = [
    { to: '/', icon: Home, label: 'Início' },
    { to: '/history', icon: History, label: 'História' },
    { to: '/noticias', icon: Newspaper, label: 'Notícias' },
    { to: '/contact', icon: Mail, label: 'Contato' }
  ];

  return (
    <header className="bg-white shadow-lg border-b-4 border-[rgb(25,42,86)] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-3" onClick={closeMobileMenu}>
                <div className="bg-white p-2 rounded-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
                    alt="Knorr Leilões"
                    className="h-10 w-auto"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                </div>
                <div className="text-2xl font-bold text-[rgb(46,48,146)] hidden">
                  KNORR LEILÕES
                </div>
              </Link>
              
              {/* YouTube Channel Link */}
              <a
                href="https://www.youtube.com/knorrleiloes" 
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity duration-200"
                title="Canal Knorr Leilões no YouTube"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_knorr-auction/artifacts/78grqti7_Screenshot_1-removebg-preview.png"
                  alt="Canal Knorr Leilões TV"
                  className="h-10 w-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </a>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.to} to={item.to}>
                  <Button variant="ghost" className="flex items-center space-x-2 text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white transition-colors duration-200 font-bold">
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Button>
                </Link>
              );
            })}

            {user?.isLoggedIn ? (
              <div className="flex items-center space-x-4">
                {/* Profile Dropdown */}
                <div className="relative" ref={profileDropdownRef}>
                  <Button
                    variant="ghost"
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    className="flex items-center space-x-2 text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white transition-colors duration-200 font-bold"
                  >
                    <User className="h-4 w-4" />
                    <span className="max-w-32 truncate">{user.detalhes?.nomeCompleto || user.nome}</span>
                    {profileDropdownOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                  
                  {profileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-80 z-50">
                      <Card className="shadow-lg border-2 border-[rgb(25,42,86)]/20">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="border-b border-gray-200 pb-3">
                              <h3 className="font-semibold text-[rgb(25,42,86)] text-lg">Dados do Cliente</h3>
                            </div>
                            
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="font-medium text-gray-600">Nome:</span>
                                <p className="text-gray-800">{user.detalhes?.nomeCompleto || user.nome}</p>
                              </div>
                              
                              <div>
                                <span className="font-medium text-gray-600">CPF:</span>
                                <p className="text-gray-800 font-mono">{user.detalhes?.cpf || user.cpf}</p>
                              </div>
                              
                              {user.detalhes?.cnpj && (
                                <div>
                                  <span className="font-medium text-gray-600">CNPJ:</span>
                                  <p className="text-gray-800 font-mono">{user.detalhes.cnpj}</p>
                                </div>
                              )}
                              
                              {user.detalhes?.email && (
                                <div>
                                  <span className="font-medium text-gray-600">Email:</span>
                                  <p className="text-gray-800">{user.detalhes.email}</p>
                                </div>
                              )}
                              
                              <div>
                                <span className="font-medium text-gray-600">ID Cliente:</span>
                                <p className="text-gray-800 font-mono">#{user.clienteId}</p>
                              </div>
                            </div>
                            
                            <div className="border-t border-gray-200 pt-3 space-y-2">
                              {isAdmin && (
                                <>
                                  <Link
                                    to="/admin/catalogos"
                                    onClick={() => setProfileDropdownOpen(false)}
                                    className="block w-full"
                                  >
                                    <Button 
                                      variant="outline" 
                                      className="w-full justify-center border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-bold"
                                    >
                                      <Upload className="h-4 w-4 mr-2" />
                                      Gerenciar Catálogos
                                    </Button>
                                  </Link>
                                  <Link
                                    to="/admin/imoveis"
                                    onClick={() => setProfileDropdownOpen(false)}
                                    className="block w-full"
                                  >
                                    <Button 
                                      variant="outline" 
                                      className="w-full justify-center border-green-600 text-green-600 hover:bg-green-600 hover:text-white font-bold"
                                    >
                                      <Building className="h-4 w-4 mr-2" />
                                      Negócios Imobiliários
                                    </Button>
                                  </Link>
                                  <Link
                                    to="/admin/compras"
                                    onClick={() => setProfileDropdownOpen(false)}
                                    className="block w-full"
                                  >
                                    <Button 
                                      variant="outline" 
                                      className="w-full justify-center border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white font-bold"
                                    >
                                      <ShoppingCart className="h-4 w-4 mr-2" />
                                      Compras de Clientes
                                    </Button>
                                  </Link>
                                </>
                              )}
                              
                              <Link
                                to="/meus-lances"
                                onClick={() => setProfileDropdownOpen(false)}
                                className="block w-full"
                              >
                                <Button 
                                  variant="outline" 
                                  className="w-full justify-center border-[rgb(25,42,86)] text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white font-bold"
                                >
                                  <Gavel className="h-4 w-4 mr-2" />
                                  Meus Lances
                                </Button>
                              </Link>
                              
                              <Link
                                to="/faturas"
                                onClick={() => setProfileDropdownOpen(false)}
                                className="block w-full"
                              >
                                <Button 
                                  variant="outline" 
                                  className="w-full justify-center border-[rgb(25,42,86)] text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white font-bold"
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  Minhas Faturas
                                </Button>
                              </Link>
                              
                              <Link
                                to="/perfil"
                                onClick={() => setProfileDropdownOpen(false)}
                                className="block w-full"
                              >
                                <Button 
                                  variant="outline" 
                                  className="w-full justify-center border-[rgb(25,42,86)] text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white font-bold"
                                >
                                  <User className="h-4 w-4 mr-2" />
                                  Ver Perfil Completo
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
                
                <Button 
                  onClick={handleLogout}
                  variant="outline" 
                  className="flex items-center space-x-2 border-[rgb(238,29,37)] text-[rgb(238,29,37)] hover:bg-[rgb(238,29,37)] hover:text-white transition-colors duration-200"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sair</span>
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <a href="https://wa.me/5553981580330?text=Olá! Gostaria de me cadastrar no Knorr Leilões." target="_blank" rel="noopener noreferrer">
                  <Button 
                    variant="outline" 
                    className="flex items-center space-x-2 border-[rgb(25,42,86)] text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white transition-colors duration-200 font-bold"
                  >
                    <UserPlus className="h-4 w-4" />
                    <span>Cadastre-se</span>
                  </Button>
                </a>
                <Link to="/login">
                  <Button 
                    variant="default" 
                    className="flex items-center space-x-2 bg-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)]/90 transition-colors duration-200"
                  >
                    <LogIn className="h-4 w-4" />
                    <span>Entrar</span>
                  </Button>
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)] hover:text-white"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.to} to={item.to} onClick={closeMobileMenu}>
                    <Button 
                      variant="ghost" 
                      className="w-full justify-start flex items-center space-x-3 text-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)] hover:text-white transition-colors duration-200"
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-base">{item.label}</span>
                    </Button>
                  </Link>
                );
              })}

              <div className="border-t border-gray-200 pt-4 pb-3">
                {user?.isLoggedIn ? (
                  <div className="space-y-3">
                    <div className="px-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-base font-semibold text-[rgb(46,48,146)] mb-2">
                          Dados do Cliente
                        </p>
                        <div className="space-y-1 text-sm">
                          <p><span className="font-medium">Nome:</span> {user.detalhes?.nomeCompleto || user.nome}</p>
                          <p><span className="font-medium">CPF:</span> {user.detalhes?.cpf || user.cpf}</p>
                          {user.detalhes?.cnpj && (
                            <p><span className="font-medium">CNPJ:</span> {user.detalhes.cnpj}</p>
                          )}
                          {user.detalhes?.email && (
                            <p><span className="font-medium">Email:</span> {user.detalhes.email}</p>
                          )}
                          <p><span className="font-medium">ID Cliente:</span> #{user.clienteId}</p>
                        </div>
                      </div>
                    </div>
                    <Link to="/meus-lances" onClick={closeMobileMenu}>
                      <Button 
                        variant="ghost"
                        className="w-full justify-start flex items-center space-x-3 text-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)] hover:text-white transition-colors duration-200"
                      >
                        <Gavel className="h-5 w-5" />
                        <span className="text-base">Meus Lances</span>
                      </Button>
                    </Link>
                    <Link to="/faturas" onClick={closeMobileMenu}>
                      <Button 
                        variant="ghost"
                        className="w-full justify-start flex items-center space-x-3 text-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)] hover:text-white transition-colors duration-200"
                      >
                        <FileText className="h-5 w-5" />
                        <span className="text-base">Minhas Faturas</span>
                      </Button>
                    </Link>
                    {isAdmin && (
                      <>
                        <Link to="/admin/catalogos" onClick={closeMobileMenu}>
                          <Button 
                            variant="ghost"
                            className="w-full justify-start flex items-center space-x-3 text-orange-500 hover:bg-orange-500 hover:text-white transition-colors duration-200"
                          >
                            <Upload className="h-5 w-5" />
                            <span className="text-base">Gerenciar Catálogos</span>
                          </Button>
                        </Link>
                        <Link to="/admin/imoveis" onClick={closeMobileMenu}>
                          <Button 
                            variant="ghost"
                            className="w-full justify-start flex items-center space-x-3 text-green-600 hover:bg-green-600 hover:text-white transition-colors duration-200"
                          >
                            <Building className="h-5 w-5" />
                            <span className="text-base">Negócios Imobiliários</span>
                          </Button>
                        </Link>
                        <Link to="/admin/compras" onClick={closeMobileMenu}>
                          <Button 
                            variant="ghost"
                            className="w-full justify-start flex items-center space-x-3 text-purple-600 hover:bg-purple-600 hover:text-white transition-colors duration-200"
                          >
                            <ShoppingCart className="h-5 w-5" />
                            <span className="text-base">Compras de Clientes</span>
                          </Button>
                        </Link>
                      </>
                    )}
                    <Button 
                      onClick={handleLogout}
                      variant="outline" 
                      className="w-full justify-start flex items-center space-x-3 border-[rgb(238,29,37)] text-[rgb(238,29,37)] hover:bg-[rgb(238,29,37)] hover:text-white transition-colors duration-200"
                    >
                      <LogOut className="h-5 w-5" />
                      <span className="text-base">Sair</span>
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <a href="https://wa.me/5553981580330?text=Olá! Gostaria de me cadastrar no Knorr Leilões." target="_blank" rel="noopener noreferrer" onClick={closeMobileMenu}>
                      <Button 
                        variant="outline"
                        className="w-full justify-start flex items-center space-x-3 border-[rgb(46,48,146)] text-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)] hover:text-white transition-colors duration-200"
                      >
                        <UserPlus className="h-5 w-5" />
                        <span className="text-base">Cadastre-se</span>
                      </Button>
                    </a>
                    <Link to="/login" onClick={closeMobileMenu}>
                      <Button 
                        className="w-full justify-start flex items-center space-x-3 bg-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)]/90 text-white transition-colors duration-200"
                      >
                        <LogIn className="h-5 w-5" />
                        <span className="text-base">Entrar</span>
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;